import React, { useMemo } from "react";
import { Grid, Typography, Box } from "@material-ui/core";
import intl from "react-intl-universal";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LegendValueFormatter,
  TooltipFormatter,
} from "recharts";

import { IWithballData } from "types";
import { StatisticsBoard } from "components/StatisticsBoard";
import { BAR_COLOR } from "MuiProvider/colors";
import {
  summarisePass,
  buildPersonalPassMatrix,
  calculatePecentage,
} from "utils";

interface IMyPassPanelProps {
  dataArray: IWithballData[];
  playerId: string;
}

const ASPECT = 2;
const CHART_WIDTH = "80%";
const BAR_SIZE = 20;

export const MyPassPanel = ({ dataArray, playerId }: IMyPassPanelProps) => {
  const { summary, matrix } = useMemo(() => {
    const myData = dataArray.filter(
      (data) => data.fromPlayer.toString() === playerId
    );
    return {
      summary: summarisePass(myData),
      matrix: buildPersonalPassMatrix(playerId, dataArray),
    };
  }, [dataArray, playerId]);

  const tooltipFormatter: TooltipFormatter = (value) => {
    const label = intl.get("MATCH_TEAM_PERSONAL_VIEW_PASS_CHART_TOTAL");
    return [value, label];
  };

  const legendFormatter: LegendValueFormatter = (value, entry) => {
    const label = intl.get("MATCH_TEAM_PERSONAL_VIEW_PASS_CHART_TOTAL");
    return <span style={{ color: entry?.color }}>{label}</span>;
  };

  const renderPassToOtherChart = () => {
    if (!matrix.passToOtherMatrix) {
      return "No data";
    }

    const data = Object.getOwnPropertyNames(matrix.passToOtherMatrix).map(
      (property) => {
        return {
          name:
            property === "-1"
              ? intl.get("MATCH_STATS_UNKNOWN_PLAYER")
              : matrix.passToOtherMatrix[property][0].toName,
          total: matrix.passToOtherMatrix[property].length,
        };
      }
    );

    if (data.length <= 0) {
      return null;
    }

    return (
      <Grid item xs={6}>
        <Typography variant="h4">
          {intl.get("MATCH_TEAM_PERSONAL_VIEW_PASS_TO_CHART_TITLE")}
        </Typography>
        <Box mt={2} />
        <ResponsiveContainer width={CHART_WIDTH} aspect={ASPECT}>
          <BarChart
            data={data}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <XAxis dataKey="name" angle={-45} interval={0} />
            <YAxis />
            <Tooltip formatter={tooltipFormatter} />
            <Legend formatter={legendFormatter} />
            <Bar dataKey="total" fill={BAR_COLOR.total} barSize={BAR_SIZE} />
          </BarChart>
        </ResponsiveContainer>
      </Grid>
    );
  };

  const renderOtherPassToChart = () => {
    if (!matrix.otherPassToMeMatrix) {
      return "No data";
    }

    const data = Object.getOwnPropertyNames(matrix.otherPassToMeMatrix).map(
      (property) => {
        return {
          name:
            property === "-1"
              ? intl.get("MATCH_STATS_UNKNOWN_PLAYER")
              : matrix.otherPassToMeMatrix[property][0].fromName,
          total: matrix.otherPassToMeMatrix[property].length,
        };
      }
    );

    if (data.length <= 0) {
      return null;
    }

    return (
      <Grid item xs={6}>
        <Typography variant="h4">
          {intl.get("MATCH_TEAM_PERSONAL_VIEW_PASS_FROM_CHART_TITLE")}
        </Typography>
        <Box mt={2} />
        <ResponsiveContainer width={CHART_WIDTH} aspect={ASPECT}>
          <BarChart
            data={data}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <XAxis dataKey="name" angle={-45} interval={0} />
            <YAxis />
            <Tooltip formatter={tooltipFormatter} />
            <Legend formatter={legendFormatter} />
            <Bar dataKey="total" fill={BAR_COLOR.total} barSize={BAR_SIZE} />
          </BarChart>
        </ResponsiveContainer>
      </Grid>
    );
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={4}>
          <Typography>{`${intl.get("MATCH_TEAM_OVERVIEW_PASS_TOTAL")}: ${
            summary.total
          }`}</Typography>
          <Typography>{`${intl.get("MATCH_TEAM_OVERVIEW_PASS_SUCC")}: ${
            summary.succ
          }`}</Typography>
          <Typography>{`${intl.get(
            "MATCH_TEAM_OVERVIEW_PASS_SUCC_RATE"
          )}: ${calculatePecentage(summary.succ, summary.total)}`}</Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography>{`${intl.get(
            "MATCH_TEAM_OVERVIEW_OWN_HALF_PASS_TOTAL"
          )}: ${summary.selfHalf.total}`}</Typography>
          <Typography>{`${intl.get("MATCH_TEAM_OVERVIEW_PASS_SUCC")}: ${
            summary.selfHalf.succ
          }`}</Typography>
          <Typography>{`${intl.get(
            "MATCH_TEAM_OVERVIEW_PASS_SUCC_RATE"
          )}: ${calculatePecentage(
            summary.selfHalf.succ,
            summary.selfHalf.total
          )}`}</Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography>{`${intl.get(
            "MATCH_TEAM_OVERVIEW_OTHER_HALF_PASS_TOTAL"
          )}: ${summary.otherHalf.total}`}</Typography>
          <Typography>{`${intl.get("MATCH_TEAM_OVERVIEW_PASS_SUCC")}: ${
            summary.otherHalf.succ
          }`}</Typography>
          <Typography>{`${intl.get(
            "MATCH_TEAM_OVERVIEW_PASS_SUCC_RATE"
          )}: ${calculatePecentage(
            summary.otherHalf.succ,
            summary.otherHalf.total
          )}`}</Typography>
        </Grid>
      </Grid>
      <Box mt={2} />
      <Grid container spacing={2}>
        <Grid item xs={4}>
          <Typography>{`${intl.get(
            "MATCH_TEAM_OVERVIEW_FORWARD_PASS_TOTAL"
          )}: ${summary.passForward.total}`}</Typography>
          <Typography>{`${intl.get("MATCH_TEAM_OVERVIEW_PASS_SUCC")}: ${
            summary.passForward.succ
          }`}</Typography>
          <Typography>{`${intl.get(
            "MATCH_TEAM_OVERVIEW_PASS_SUCC_RATE"
          )}: ${calculatePecentage(
            summary.passForward.succ,
            summary.passForward.total
          )}`}</Typography>
          <Box mt={2} />
          <StatisticsBoard data={summary.passForward.data} />
        </Grid>
        <Grid item xs={4}>
          <Typography>{`${intl.get("MATCH_TEAM_OVERVIEW_CROSS_PASS_TOTAL")}: ${
            summary.passCross.total
          }`}</Typography>
          <Typography>{`${intl.get("MATCH_TEAM_OVERVIEW_PASS_SUCC")}: ${
            summary.passCross.succ
          }`}</Typography>
          <Typography>{`${intl.get(
            "MATCH_TEAM_OVERVIEW_PASS_SUCC_RATE"
          )}: ${calculatePecentage(
            summary.passCross.succ,
            summary.passCross.total
          )}`}</Typography>
          <Box mt={2} />
          <StatisticsBoard data={summary.passCross.data} />
        </Grid>
        <Grid item xs={4}>
          <Typography>{`${intl.get(
            "MATCH_TEAM_OVERVIEW_BACKWARD_PASS_TOTAL"
          )}: ${summary.passBackward.total}`}</Typography>
          <Typography>{`${intl.get("MATCH_TEAM_OVERVIEW_PASS_SUCC")}: ${
            summary.passBackward.succ
          }`}</Typography>
          <Typography>{`${intl.get(
            "MATCH_TEAM_OVERVIEW_PASS_SUCC_RATE"
          )}: ${calculatePecentage(
            summary.passBackward.succ,
            summary.passBackward.total
          )}`}</Typography>
          <Box mt={2} />
          <StatisticsBoard data={summary.passBackward.data} />
        </Grid>
      </Grid>
      <Box mt={2} />
      <Grid container spacing={2}>
        <Grid item xs={4}>
          <Typography>{`${intl.get("MATCH_TEAM_OVERVIEW_SHORT_PASS_TOTAL")}: ${
            summary.short.total
          }`}</Typography>
          <Typography>{`${intl.get("MATCH_TEAM_OVERVIEW_PASS_SUCC")}: ${
            summary.short.succ
          }`}</Typography>
          <Typography>{`${intl.get(
            "MATCH_TEAM_OVERVIEW_PASS_SUCC_RATE"
          )}: ${calculatePecentage(
            summary.short.succ,
            summary.short.total
          )}`}</Typography>
          <Box mt={2} />
          <StatisticsBoard data={summary.short.data} />
        </Grid>
        <Grid item xs={4}>
          <Typography>{`${intl.get("MATCH_TEAM_OVERVIEW_LONG_PASS_TOTAL")}: ${
            summary.long.total
          }`}</Typography>
          <Typography>{`${intl.get("MATCH_TEAM_OVERVIEW_PASS_SUCC")}: ${
            summary.long.succ
          }`}</Typography>
          <Typography>{`${intl.get(
            "MATCH_TEAM_OVERVIEW_PASS_SUCC_RATE"
          )}: ${calculatePecentage(
            summary.long.succ,
            summary.long.total
          )}`}</Typography>
          <Box mt={2} />
          <StatisticsBoard data={summary.long.data} />
        </Grid>
        <Grid item xs={4}>
          <Typography>{`${intl.get("MATCH_TEAM_OVERVIEW_THROW_IN_PASS_TOTAL")}: ${
            summary.throwIn.total
          }`}</Typography>
          <Typography>{`${intl.get("MATCH_TEAM_OVERVIEW_PASS_SUCC")}: ${
            summary.throwIn.succ
          }`}</Typography>
          <Typography>{`${intl.get(
            "MATCH_TEAM_OVERVIEW_PASS_SUCC_RATE"
          )}: ${calculatePecentage(
            summary.throwIn.succ,
            summary.throwIn.total
          )}`}</Typography>
          <Box mt={2} />
          <StatisticsBoard data={summary.throwIn.data} />
        </Grid>
      </Grid>
      <Box mt={2} />
      <Grid container spacing={2}>
        {renderPassToOtherChart()}
        {renderOtherPassToChart()}
      </Grid>
    </>
  );
};
